/**
 * ComfyUI VRAM Management
 *
 * Tracks task types and manages VRAM by unloading models
 * when switching between different task types (image vs TTS).
 *
 * This prevents VRAM competition between large models:
 * - Image generation: ~7-8GB for diffusion models
 * - TTS generation: ~3-4GB for Qwen3-TTS
 */

const LOCAL_COMFYUI_URL = import.meta.env.VITE_LOCAL_COMFYUI_URL || 'http://127.0.0.1:8188';

export type TaskType = 'image' | 'tts';

// Track the last task type executed
let lastTaskType: TaskType | null = null;

/**
 * Get the current task type that was last executed
 */
export function getLastTaskType(): TaskType | null {
    return lastTaskType;
}

/**
 * Free ComfyUI VRAM by unloading models
 * Call this before switching to a different task type
 */
export async function freeComfyUIMemory(): Promise<void> {
    try {
        console.log('🧹 Freeing ComfyUI VRAM...');
        const response = await fetch(`${LOCAL_COMFYUI_URL}/free`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                unload_models: true,
                free_memory: true
            })
        });

        if (response.ok) {
            console.log('✅ ComfyUI VRAM freed successfully');
        } else {
            console.warn('⚠️ Failed to free ComfyUI memory:', response.status);
        }
    } catch (error) {
        console.warn('⚠️ Could not free ComfyUI memory:', error);
    }
}

/**
 * Prepare VRAM for a new task type
 * If switching task types, free memory first to avoid competition
 *
 * @param newTaskType The type of task about to be executed
 * @returns Promise that resolves when VRAM is ready
 */
export async function prepareVRAMForTask(newTaskType: TaskType): Promise<void> {
    if (lastTaskType !== null && lastTaskType !== newTaskType) {
        console.log(`🔄 Switching from ${lastTaskType} to ${newTaskType} - freeing VRAM first`);
        await freeComfyUIMemory();

        // Small delay to ensure models are fully unloaded
        await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Update the last task type
    lastTaskType = newTaskType;
}

/**
 * Reset task tracking (useful for testing or error recovery)
 */
export function resetTaskTracking(): void {
    lastTaskType = null;
}
